import _slicedToArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
import { faClock } from "@fortawesome/pro-regular-svg-icons";
import { Input, Popover } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/button";
import { DateFieldBody } from "./date-field-body";
import { DateFieldTrigger } from "./date-field-trigger";
import { DEFAULT_DISPLAY_FORMATS, formatDisplay, formatRangeDisplay, parseCanonical, toCanonicalDate, toCanonicalDateTime } from "./date-field-value";
import { POPOVER_STYLES, POPOVER_Z_INDEX } from "./date-field-styles";
import { TimeSelect } from "./time-select";
import { formatTimeLabel } from "./time-options";
/** Generic SDS date field: single, datetime, or range with month/year dropdowns. */
export function DateField(_ref) {
  var _ref2, _ref3;
  var type = _ref.type,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? "field" : _ref$variant,
    value = _ref.value,
    onChange = _ref.onChange,
    testId = _ref.testId,
    label = _ref.label,
    placeholder = _ref.placeholder,
    displayFormat = _ref.displayFormat,
    withAsterisk = _ref.withAsterisk,
    required = _ref.required,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    error = _ref.error,
    minDate = _ref.minDate,
    maxDate = _ref.maxDate,
    showNow = _ref.showNow,
    _ref$enableMonthRange = _ref.enableMonthRange,
    enableMonthRange = _ref$enableMonthRange === void 0 ? false : _ref$enableMonthRange,
    _ref$highlightToday = _ref.highlightToday,
    highlightToday = _ref$highlightToday === void 0 ? false : _ref$highlightToday,
    inputRef = _ref.inputRef;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(false),
    opened = _useState[0],
    setOpened = _useState[1];
  var _useState2 = useState("day"),
    mode = _useState2[0],
    setMode = _useState2[1];
  var isRange = type === "range";
  var format = displayFormat !== null && displayFormat !== void 0 ? displayFormat : DEFAULT_DISPLAY_FORMATS[type];
  var rangeValue = isRange ? value : [null, null];
  var singleDate = isRange ? null : parseCanonical(value);
  var rangeDates = [parseCanonical(rangeValue[0]), parseCanonical(rangeValue[1])];
  var anchor = (_ref2 = singleDate !== null && singleDate !== void 0 ? singleDate : rangeDates[0]) !== null && _ref2 !== void 0 ? _ref2 : new Date();
  var _useState3 = useState(anchor),
    month = _useState3[0],
    setMonth = _useState3[1];
  var displayText = isRange ? formatRangeDisplay(rangeDates[0], rangeDates[1], format) : formatDisplay(singleDate, format);
  var humanFormat = format.replace(/[a-z]/gi, function (_char) {
    return _char.toUpperCase();
  });
  var resolvedPlaceholder = (_ref3 = placeholder !== null && placeholder !== void 0 ? placeholder : variant === "filter" && typeof label === "string" ? label : undefined) !== null && _ref3 !== void 0 ? _ref3 : isRange ? "".concat(humanFormat, " \u2013 ").concat(humanFormat) : humanFormat;
  var open = function open() {
    if (!opened) setMonth(anchor);
    setOpened(true);
  };
  var commitSingle = function commitSingle(date) {
    if (type === "datetime") {
      if (!date) return onChange("");
      var next = new Date(date);
      if (singleDate) next.setHours(singleDate.getHours(), singleDate.getMinutes());
      return onChange(toCanonicalDateTime(next));
    }
    onChange(toCanonicalDate(date));
  };
  var commitTime = function commitTime(time) {
    if (!time) return;
    var _time$split$map = time.split(":").map(Number),
      _time$split$map2 = _slicedToArray(_time$split$map, 2),
      hours = _time$split$map2[0],
      minutes = _time$split$map2[1];
    var next = singleDate ? new Date(singleDate) : new Date();
    next.setHours(hours !== null && hours !== void 0 ? hours : 0, minutes !== null && minutes !== void 0 ? minutes : 0, 0, 0);
    onChange(toCanonicalDateTime(next));
  };
  var commitRange = function commitRange(range) {
    return onChange([toCanonicalDate(range[0]) || null, toCanonicalDate(range[1]) || null]);
  };
  var clear = function clear() {
    return onChange(isRange ? [null, null] : "");
  };
  var setNow = function setNow() {
    return onChange(type === "datetime" ? toCanonicalDateTime(new Date()) : toCanonicalDate(new Date()));
  };
  var trigger = /*#__PURE__*/React.createElement(DateFieldTrigger, {
    inputRef: inputRef,
    displayText: displayText,
    placeholder: resolvedPlaceholder,
    displayFormat: format,
    disabled: disabled,
    invalid: Boolean(error),
    readOnly: isRange,
    testId: testId,
    onOpen: open,
    onClear: clear,
    onTypedDate: commitSingle
  });
  var popover = /*#__PURE__*/React.createElement(Popover, {
    opened: opened,
    onChange: setOpened,
    position: "bottom-start",
    withinPortal: true,
    zIndex: POPOVER_Z_INDEX,
    shadow: "md",
    disabled: disabled,
    styles: POPOVER_STYLES
  }, /*#__PURE__*/React.createElement(Popover.Target, null, /*#__PURE__*/React.createElement("div", {
    className: variant === "field" ? "flex-1" : "w-full"
  }, trigger)), /*#__PURE__*/React.createElement(Popover.Dropdown, null, /*#__PURE__*/React.createElement(DateFieldBody, {
    type: type,
    enableMonthRange: enableMonthRange,
    mode: mode,
    onModeChange: setMode,
    month: month,
    onMonthChange: setMonth,
    singleValue: singleDate,
    rangeValue: rangeDates,
    minDate: minDate,
    maxDate: maxDate,
    onSelectSingle: commitSingle,
    onSelectRange: commitRange,
    onRequestClose: function onRequestClose() {
      return setOpened(false);
    },
    highlightToday: highlightToday,
    testId: testId
  })));
  if (variant === "filter") {
    return /*#__PURE__*/React.createElement("div", {
      className: isRange ? "w-60" : "w-48"
    }, popover);
  }
  return /*#__PURE__*/React.createElement(Input.Wrapper, {
    label: label,
    withAsterisk: withAsterisk !== null && withAsterisk !== void 0 ? withAsterisk : false,
    required: required !== null && required !== void 0 ? required : false,
    error: error
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex gap-2"
  }, popover, type === "datetime" && /*#__PURE__*/React.createElement(TimeSelect, {
    value: formatTimeLabel(singleDate) || null,
    onChange: commitTime,
    invalid: Boolean(error),
    disabled: disabled,
    testId: testId
  }), showNow && /*#__PURE__*/React.createElement(Button, {
    variant: "secondary",
    size: "sm",
    leftIcon: faClock,
    onClick: setNow,
    "data-testid": "".concat(testId, "-now")
  }, t("now", {
    defaultValue: "Now"
  }))));
}